import { useMemo } from 'react';

import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell, TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import {
  GroupDefinitionListNode,
  useGroupsByIds,
} from '@/hooks/discovery/definitionGroups';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string | null;
  uniqueId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerSlack: string | null;
  ownerGithub: string | null;
  modelCount: number | undefined;
};

const getTableData = (
  group: GroupDefinitionListNode | undefined | null,
): TableData | undefined => {
  if (!group) return undefined;
  return group;
};

export const GroupFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const { userId } = useEnvironmentScope();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');

  const paginatedGroupQuery = useGroupsByIds({
    environmentId,
    userId,
    first: pageSize,
  });
  const groups = paginatedGroupQuery.data;
  const pageInfo = paginatedGroupQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return groups?.map(getTableData);
  }, [groups]);

  if (paginatedGroupQuery.error) return <>{paginatedGroupQuery.error.stack}</>;
  const isLoadingMore = paginatedGroupQuery.fetching;
  const isInitialLoad =
    paginatedGroupQuery.fetching && paginatedGroupQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Groups</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="groups"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            minSize: 60,
            maxSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: () => <OneLineHeader title="Model count" align="right" />,
            accessorKey: 'modelCount',
            cell: RightAlignedTruncatedCell,
            meta: {
              align: 'right',
            },
          },
          {
            header: 'Owner name',
            accessorKey: 'ownerName',
            cell: TruncatedCell,
          },
          {
            header: 'Owner email',
            accessorKey: 'ownerEmail',
            cell: TruncatedCell,
          },
          {
            header: 'Owner GitHub',
            accessorKey: 'ownerGithub',
            cell: TruncatedCell,
          },
          {
            header: 'Owner Slack',
            accessorKey: 'ownerSlack',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedGroupQuery.fetchMore}
      />
    </>
  );
};
